import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { TaxReportsProgressPanel } from './TaxReportsProgressPanel';
import { ReportsListView } from './ReportsListView';
import { AggregatedView } from './AggregatedView';
import { WbAggregatedView } from './WbAggregatedView';
import { WbReportsListView } from './WbReportsListView';
import { WbProgressPanel } from './WbProgressPanel';
import { formatMoney } from '../../utils/formatNumber';
import logger from "../../utils/logger";
import ToolHelpButton from '../../components/ToolHelpButton';
function TaxReportsPanel({
  accountId,
  accountName,
  marketplace
}) {
  const {
    request,
    showStatus
  } = useApi();
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [showWbProgressPanel, setShowWbProgressPanel] = useState(false);

  // OZON state
  const [ozonMonths, setOzonMonths] = useState([]);
  const [selectedOzonMonth, setSelectedOzonMonth] = useState('');
  const [ozonData, setOzonData] = useState(null);

  // WB state
  const [wbWeeks, setWbWeeks] = useState([]);
  const [selectedWbWeek, setSelectedWbWeek] = useState('');
  const [wbData, setWbData] = useState(null);
  const isOzon = marketplace === 'ozon';
  const isWb = marketplace === 'wildberries';
  useEffect(() => {
    if (isOzon) {
      loadOzonMonths();
    } else if (isWb) {
      loadWbWeeks();
    }
  }, [accountId, marketplace]);

  // ============================================================
  // OZON Methods
  // ============================================================

  const loadOzonMonths = async () => {
    try {
      const response = await request(`/api/v1/tax-reports/ozon/${accountId}/months`);
      setOzonMonths(response.months || []);
      if (response.months && response.months.length > 0) {
        setSelectedOzonMonth(response.months[0]);
      }
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const loadOzonMonthData = async period => {
    if (!period) return;
    setLoading(true);
    try {
      const response = await request(`/api/v1/tax-reports/ozon/${accountId}/monthly/${period}`);
      setOzonData(response);
      showStatus('success', 'Данные загружены');
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const fetchOzonReport = async period => {
    if (!period) return;
    setLoading(true);
    try {
      await request(`/api/v1/tax-reports/ozon/${accountId}/fetch/${period}`, {
        method: 'POST'
      });
      showStatus('success', `Отчет за ${period} загружен`);
      await loadOzonMonths();
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const fetchAllOzonReports = async () => {
    if (!window.confirm('Загрузить все отчеты с 08.2022 до текущего периода? Это может занять несколько минут.')) {
      return;
    }
    setLoading(true);
    try {
      const response = await request(`/api/v1/tax-reports/ozon/${accountId}/fetch-all?start_period=2022-08`, {
        method: 'POST'
      });
      showStatus('success', `Загрузка завершена: ${response.successful} успешно, ${response.failed} ошибок`);
      await loadOzonMonths();
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const fetchMissingOzonReports = async () => {
    setLoading(true);
    try {
      // First check what's missing
      const checkResponse = await request(`/api/v1/tax-reports/ozon/${accountId}/missing-periods?start_period=2022-08`);
      if (checkResponse.count === 0) {
        showStatus('success', 'Все отчеты уже загружены');
        return;
      }
      if (!window.confirm(`Найдено ${checkResponse.count} недостающих периодов. Загрузить их?`)) {
        return;
      }
      const response = await request(`/api/v1/tax-reports/ozon/${accountId}/fetch-missing?start_period=2022-08`, {
        method: 'POST'
      });
      showStatus('success', `Дозагрузка завершена: ${response.successful} успешно, ${response.failed} ошибок`);
      await loadOzonMonths();
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // WB Methods
  // ============================================================

  const loadWbWeeks = async () => {
    try {
      const response = await request(`/api/v1/tax-reports/wb/v2/${accountId}/progress`);
      logger.info('WB Progress Response:', response);
      logger.info('Periods:', response.periods);

      // Extract weeks from periods array - ONLY fetched ones
      const weeks = response.periods?.filter(p => p.fetch_status === 'fetched') // Only successfully fetched
      .map(p => p.period).filter(week => week && typeof week === 'string') || [];
      logger.info('Extracted weeks:', weeks);
      setWbWeeks(weeks);
      if (weeks.length > 0) {
        setSelectedWbWeek(weeks[0]);
      }

      // Show status message
      if (response.total > 0) {
        showStatus('info', `WB отчёты: загружено ${response.fetched}, в процессе ${response.pending}, ошибок ${response.error}`);
      }
    } catch (e) {
      logger.error('loadWbWeeks error:', e);
      showStatus('error', String(e.message || e));
    }
  };
  const loadWbWeekData = async weekPeriod => {
    if (!weekPeriod) return;
    setLoading(true);
    try {
      const response = await request(`/api/v1/tax-reports/wb/${accountId}/details/${encodeURIComponent(weekPeriod)}?page=1&page_size=100`);
      setWbData(response);
      showStatus('success', 'Данные загружены');
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Delete Methods
  // ============================================================

  const deleteOzonMonth = async month => {
    setLoading(true);
    try {
      await request(`/api/v1/tax-reports/ozon/${accountId}/period/${month}`, {
        method: 'DELETE'
      });
      showStatus('success', `Период ${month} удалён`);
      await loadOzonMonths();
      // Clear selected data if it was deleted
      if (selectedOzonMonth === month) {
        setOzonData(null);
      }
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const deleteAllOzonData = async () => {
    setLoading(true);
    try {
      await request(`/api/v1/tax-reports/ozon/${accountId}/all`, {
        method: 'DELETE'
      });
      showStatus('success', 'Все OZON отчёты удалены');
      setOzonMonths([]);
      setSelectedOzonMonth('');
      setOzonData(null);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const deleteWbWeek = async week => {
    setLoading(true);
    try {
      await request(`/api/v1/tax-reports/wb/${accountId}/period/${encodeURIComponent(week)}`, {
        method: 'DELETE'
      });
      showStatus('success', `Период ${week} удалён`);
      await loadWbWeeks();
      // Clear selected data if it was deleted
      if (selectedWbWeek === week) {
        setWbData(null);
      }
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  const deleteAllWbData = async () => {
    setLoading(true);
    try {
      await request(`/api/v1/tax-reports/wb/${accountId}/all`, {
        method: 'DELETE'
      });
      showStatus('success', 'Все WB отчёты удалены');
      setWbWeeks([]);
      setSelectedWbWeek('');
      setWbData(null);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (!isOzon && !isWb) {
    return <div className="panel">
                <h2>Налоговые отчеты</h2>
                <p>Данный тип маркетплейса не поддерживает налоговые отчеты</p>
            </div>;
  }
  return <div className="panel">
            <h2>Налоговые отчеты - {accountName}{isOzon && <ToolHelpButton toolKey="ozon_tax_reports" />}</h2>
            <p className="subtitle">{isOzon ? 'OZON: Взаимозачеты' : 'Wildberries: Детализация'}</p>

            {/* Tabs */}
            <div className="tabs">
                <button className={activeTab === 'list' ? 'active' : ''} onClick={() => setActiveTab('list')}>

                    Список отчетов
                </button>
                <button className={activeTab === 'aggregated' ? 'active' : ''} onClick={() => setActiveTab('aggregated')}>

                    Агрегированный отчёт
                </button>
                <button className={activeTab === 'view' ? 'active' : ''} onClick={() => setActiveTab('view')}>

                    Просмотр данных
                </button>
            </div>

            {/* List Tab */}
            {activeTab === 'list' && <div className="tab-content">
                    {isOzon && <>
                            <ReportsListView months={ozonMonths} onViewMonth={month => {
          setSelectedOzonMonth(month);
          setActiveTab('view');
          loadOzonMonthData(month);
        }} onOpenProgressPanel={() => setShowProgressPanel(true)} onDeleteMonth={deleteOzonMonth} onDeleteAll={deleteAllOzonData} />


                            {showProgressPanel && <TaxReportsProgressPanel accountId={accountId} onClose={() => setShowProgressPanel(false)} onComplete={() => {
          loadOzonMonths();
        }} />}
                        </>}

                    {isWb && <>
                            <WbReportsListView weeks={wbWeeks} onViewWeek={week => {
          setSelectedWbWeek(week);
          setActiveTab('view');
          loadWbWeekData(week);
        }} onOpenProgressPanel={() => setShowWbProgressPanel(true)} onDeleteWeek={deleteWbWeek} onDeleteAll={deleteAllWbData} onRefresh={loadWbWeeks} />


                            {showWbProgressPanel && <WbProgressPanel accountId={accountId} onClose={() => setShowWbProgressPanel(false)} onComplete={() => {
          loadWbWeeks();
        }} />}
                        </>}
                </div>}

            {/* Aggregated Tab */}
            {activeTab === 'aggregated' && <div className="tab-content">
                    {isOzon && <AggregatedView accountId={accountId} />}
                    {isWb && <WbAggregatedView accountId={accountId} />}
                </div>}

            {/* View Tab */}
            {activeTab === 'view' && <div className="tab-content">
                    {isOzon && selectedOzonMonth && <>
                            <h3>OZON: {selectedOzonMonth}</h3>
                            <button onClick={() => loadOzonMonthData(selectedOzonMonth)} disabled={loading}>
                                {loading ? 'Загрузка...' : 'Обновить'}
                            </button>

                            {ozonData && <>
                                    {/* Monthly Totals */}
                                    {ozonData.monthly && <div className="summary-card">
                                            <h4>Итоги месяца</h4>
                                            <table>
                                                <tbody>
                                                    <tr>
                                                        <td>Расходы (без кредитов):</td>
                                                        <td>{formatMoney(ozonData.monthly.expenses)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Доходы:</td>
                                                        <td>{formatMoney(ozonData.monthly.income)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Нетто:</td>
                                                        <td><strong>{formatMoney(ozonData.monthly.net)}</strong></td>
                                                    </tr>
                                                    <tr>
                                                        <td>Платежи по кредитам:</td>
                                                        <td>{formatMoney(ozonData.monthly.credit_payments)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>}

                                    {/* Operations */}
                                    <h4>Операции (первые 100)</h4>
                                    {ozonData.operations && ozonData.operations.length > 0 ? <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Наименование</th>
                                                    <th>Документ</th>
                                                    <th>Дата</th>
                                                    <th>Дебет</th>
                                                    <th>Кредит</th>
                                                    <th>Комментарий</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ozonData.operations.map((op, idx) => <tr key={idx}>
                                                        <td>{op.name}</td>
                                                        <td>{op.document || '—'}</td>
                                                        <td>{op.date || '—'}</td>
                                                        <td>{formatMoney(op.debit)}</td>
                                                        <td>{formatMoney(op.credit)}</td>
                                                        <td>{op.comment || '—'}</td>
                                                    </tr>)}
                                            </tbody>
                                        </table> : <p>Нет операций</p>}
                                </>}
                        </>}

                    {isWb && selectedWbWeek && <>
                            <h3>Wildberries: {selectedWbWeek}</h3>
                            <button onClick={() => loadWbWeekData(selectedWbWeek)} disabled={loading}>
                                {loading ? 'Загрузка...' : 'Обновить'}
                            </button>

                            {wbData && wbData.details && <>
                                    {/* Summary cards */}
                                    {(() => {
            const details = wbData.details;
            const sum = field => details.reduce((acc, d) => acc + (d[field] || 0), 0);
            const salesCount = details.filter(d => d.supplier_oper_name === 'Продажа').length;
            const returnsCount = details.filter(d => d.supplier_oper_name === 'Возврат').length;
            return <div className="wb-summary-cards" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px',
              margin: '16px 0',
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #4caf50'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Выручка (retail)</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#333'
                }}>{formatMoney(sum('retail_amount'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #2196f3'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>К перечислению</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#2196f3'
                }}>{formatMoney(sum('ppvz_for_pay'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #ff9800'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Комиссия WB</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#ff9800'
                }}>{formatMoney(sum('ppvz_sales_commission'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #9c27b0'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Вознагр. WB</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#9c27b0'
                }}>{formatMoney(sum('ppvz_reward'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #607d8b'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Логистика</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#607d8b'
                }}>{formatMoney(sum('delivery_rub'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #e91e63'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Штрафы</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#e91e63'
                }}>{formatMoney(sum('penalty'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #795548'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Хранение</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#795548'
                }}>{formatMoney(sum('storage_fee'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #009688'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Эквайринг</div>
                                                    <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#009688'
                }}>{formatMoney(sum('acquiring_fee'))}</div>
                                                </div>
                                                <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #3f51b5'
              }}>
                                                    <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Строк / Продажи / Возвраты</div>
                                                    <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>{wbData.total} / {salesCount} / {returnsCount}</div>
                                                </div>
                                            </div>;
          })()}

                                    {/* Full detail table */}
                                    <div style={{
            overflowX: 'auto',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
                                        <table className="data-table" style={{
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}>
                                            <thead>
                                                <tr>
                                                    <th>Операция</th>
                                                    <th>Артикул WB</th>
                                                    <th>Артикул</th>
                                                    <th>Предмет</th>
                                                    <th>Бренд</th>
                                                    <th>Размер</th>
                                                    <th>Штрихкод</th>
                                                    <th>Кол-во</th>
                                                    <th>Цена розн.</th>
                                                    <th>Цена со скид.</th>
                                                    <th>Выручка</th>
                                                    <th>Скидка %</th>
                                                    <th>Комиссия %</th>
                                                    <th>К перечисл.</th>
                                                    <th>Комиссия WB</th>
                                                    <th>Вознагр. WB</th>
                                                    <th>СПП %</th>
                                                    <th>Эквайринг</th>
                                                    <th>Логистика</th>
                                                    <th>Штраф</th>
                                                    <th>Хранение</th>
                                                    <th>Удержания</th>
                                                    <th>Приемка</th>
                                                    <th>Доп. оплата</th>
                                                    <th>Склад</th>
                                                    <th>Дата продажи</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {wbData.details.map((d, idx) => <tr key={idx} style={{
                  background: d.supplier_oper_name === 'Возврат' ? '#fff3f3' : d.supplier_oper_name === 'Продажа' ? '#f3fff3' : 'inherit'
                }}>
                                                        <td><strong>{d.supplier_oper_name || '—'}</strong></td>
                                                        <td>{d.nm_id || '—'}</td>
                                                        <td>{d.sa_name || '—'}</td>
                                                        <td>{d.subject_name || '—'}</td>
                                                        <td>{d.brand_name || '—'}</td>
                                                        <td>{d.ts_name || '—'}</td>
                                                        <td>{d.barcode || '—'}</td>
                                                        <td>{d.quantity}</td>
                                                        <td>{formatMoney(d.retail_price)}</td>
                                                        <td>{formatMoney(d.retail_price_withdisc_rub)}</td>
                                                        <td style={{
                    fontWeight: 'bold'
                  }}>{formatMoney(d.retail_amount)}</td>
                                                        <td>{d.sale_percent}%</td>
                                                        <td>{d.commission_percent}%</td>
                                                        <td style={{
                    fontWeight: 'bold',
                    color: '#2196f3'
                  }}>{formatMoney(d.ppvz_for_pay)}</td>
                                                        <td style={{
                    color: '#ff9800'
                  }}>{formatMoney(d.ppvz_sales_commission)}</td>
                                                        <td>{formatMoney(d.ppvz_reward)}</td>
                                                        <td>{d.ppvz_spp_prc}%</td>
                                                        <td>{formatMoney(d.acquiring_fee)}</td>
                                                        <td>{formatMoney(d.delivery_rub)}</td>
                                                        <td style={{
                    color: d.penalty ? '#e91e63' : 'inherit'
                  }}>{formatMoney(d.penalty)}</td>
                                                        <td>{formatMoney(d.storage_fee)}</td>
                                                        <td>{formatMoney(d.deduction)}</td>
                                                        <td>{formatMoney(d.acceptance)}</td>
                                                        <td>{formatMoney(d.additional_payment)}</td>
                                                        <td>{d.office_name || '—'}</td>
                                                        <td>{d.sale_dt ? d.sale_dt.substring(0, 10) : '—'}</td>
                                                    </tr>)}
                                            </tbody>
                                        </table>
                                    </div>
                                </>}
                        </>}
                </div>}
        </div>;
}
export default TaxReportsPanel;
